import { Button, Flex, HStack, Input, Stack, useDisclosure } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-regular-svg-icons';
import { FC, useCallback, useContext, useRef, useState } from 'react';
import { useDefaultStyles } from '../../../../hooks/useDefaultStyles';
import { Loading } from '../../../../lib/components/Loading';
import { SPLIT_MARKER } from '../Forum2';
import { useDevice } from '../../../../hooks/useDevice';
import { WholeAppContext } from '../../../agenda/WholeApp.context';
import { CommentDtoProps, ContentType } from '../types/forum2.types';
import { Forum2Context } from '../../../../contexts/Forum2.context';
import { ForumTutorialButton } from './ForumTutorialButton.component';
import TutorialModal from '../../../../lib/components/TutorialModal';

type ButtonsProps = {
  onCancel: () => void,
  onPublish: () => void,
}

type CommentBoxType = {
  onClick: (comment: CommentDtoProps) => void,
  loading: boolean,
  contentId: string,
  courseId: string,
  parentId?: string,
  level: number,
  contentType: ContentType,
  showTutorial?: boolean,
  cancel?: () => void,
}

const Buttons: FC<ButtonsProps> = ({
  onCancel,
  onPublish,
}) => {
  const { colors } = useDefaultStyles();

  return (
    <>
      <Button
        color={colors.darkerGray.goLighter}
        onClick={onCancel}
        marginRight={4}
        backgroundColor={colors.background}
        size="md"
      >
        cancelar
      </Button>
      <Button
        color={colors.white.goPallete50}
        onClick={onPublish}
        _hover={{
          backgroundColor: colors.lightSecondary.keep,
        }}
        backgroundColor={colors.secondary.goDarker}
        size="md"
      >
        <FontAwesomeIcon icon={faPaperPlane} />
        &nbsp;&nbsp;
        publicar
      </Button>
    </>
  );
};

export const CommentBox: FC<CommentBoxType> = ({
  onClick,
  loading,
  contentId,
  courseId,
  parentId,
  level,
  contentType,
  showTutorial = false,
  cancel,
}) => {
  const { colors } = useDefaultStyles();
  const device = useDevice();
  const { setAreShortcutsEnabled, areShortcutsEnabled } = useContext(WholeAppContext);
  const { onCreateComment } = useContext(Forum2Context);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loadingSendComment, setLoadingSendComment] = useState(false);
  const [answering, setAnswering] = useState(false);
  const [commentBeingCreated, setCommentBeingCreated] = useState('');
  const [hasAutoDisabledShortcut, setHasAutoDisabledShortcut] = useState(false);

  const commentBoxRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(async () => {
    setLoadingSendComment(true);
    const res = await onCreateComment({
      body: commentBeingCreated,
      contentId,
      courseIdFromParam: courseId,
      parentId,
      level,
      contentType,
    });
    onClick(res);
    setAnswering(false);
    setLoadingSendComment(false);
    setCommentBeingCreated('');
    commentBoxRef.current?.blur();
    if (cancel) {
      cancel();
    }
  }, [commentBeingCreated, contentId, contentType,
    courseId, level, onClick, onCreateComment, parentId, cancel]);

  const handleEnterPress = useCallback(
    e => {
      if (e.key === 'Enter') {
        handleClick();
      }
    },
    [handleClick],
  );

  const onCancel = useCallback(() => {
    setCommentBeingCreated('');
    setAnswering(false);
    if (cancel) {
      console.log('entrou');
      cancel();
    }
  }, [cancel]);

  return (
    <>
      <TutorialModal
        onClose={onClose}
        open={isOpen}
        title="Como o Harrison-AI funciona?"
        // eslint-disable-next-line max-len
        description="Descubra como o HarrisonAI pode ser um grande aliado no fórum, respondendo suas dúvidas instantaneamente com base em livros acadêmicos."
        firebaseTutorialName="forum_tutorial_video_url"
      />
      <Flex flexFlow="column">
        <HStack
          border="1px solid"
          borderColor="gray.300"
          bgColor={loadingSendComment || loading ? colors.lightGray.goAlmostFullDarker : undefined}
          borderRadius={4}
          minH={10}
          px={2}
          mb={2}
          mt={2}
          w="100%"
        >
          <Input
            resize="none"
            ref={commentBoxRef}
            _placeholder={{ fontSize: device === 'mobile' ? 'xs' : undefined }}
            value={commentBeingCreated}
            rows={1}
            onFocus={() => setAnswering(true)}
            onBlur={() => setTimeout(() => setAnswering(false), 300)}
            onChange={c => {
              setCommentBeingCreated(c.target.value.replaceAll(SPLIT_MARKER, ''));
              if (areShortcutsEnabled) {
                setAreShortcutsEnabled(false);
                setHasAutoDisabledShortcut(true);
              }
              if (hasAutoDisabledShortcut && c.target.value === '') {
                setAreShortcutsEnabled(true);
                setHasAutoDisabledShortcut(false);
              }
            }}
            onKeyDown={handleEnterPress}
            disabled={loadingSendComment}
            color={loadingSendComment ? 'gray' : undefined}
            variant="unstyled"
            height="56px"
            placeholder="Escreva um comentário..."
          />
        </HStack>
        <Stack
          direction={device === 'web' ? 'row' : 'column'}
          justify={showTutorial ? 'space-between' : 'flex-end'}
          alignItems={device === 'web' ? 'center' : undefined}
        >
          {showTutorial && (answering || commentBeingCreated) && (
            <div>
              <ForumTutorialButton onOpen={onOpen} />
            </div>
          )}
          {showTutorial && device === 'web' ? (
            <div>
              {loadingSendComment || loading ? (
                <Loading />
              ) : (answering || commentBeingCreated) && (
                <Buttons onCancel={onCancel} onPublish={handleClick} />
              )}
            </div>
          ) : (
            <Flex justify="flex-end">
              {loadingSendComment || loading ? (
                <Loading />
              ) : (answering || commentBeingCreated) && (
                <Buttons onCancel={onCancel} onPublish={handleClick} />
              )}
            </Flex>
          )}
        </Stack>
      </Flex>
    </>
  );
};
